import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  Heart,
  Images,
  Loader2,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import TopBar from "@/components/TopBar";
import CollectibleCard from "@/components/CollectibleCard";

const emptyProgress = {
  discovered_artworks: [],
  favorite_artwork_id: null,
  status: "STARTED",
};

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

export default function MuseumExplorer({ data, user, progress, setProgress }) {
  const {
    walk,
    artworks = [],
    artwork_credits: artworkCredits = [],
    contributors = [],
    discovery_items: discoveryItems = [],
  } = data;
  const guestKey = `spuren_museum_guest:${walk.id}`;
  const [guestProgress, setGuestProgress] = useState(emptyProgress);
  const [guestLoaded, setGuestLoaded] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const dialogRef = useRef(null);
  useEffect(() => {
    if (!selectedArtwork) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const dialog = dialogRef.current;
    dialog?.querySelector("button")?.focus();
    const handleKey = (event) => {
      if (event.key === "Escape") { event.preventDefault(); setSelectedArtwork(null); return; }
      if (event.key !== "Tab") return;
      const elements = [...(dialog?.querySelectorAll('button:not([disabled]), a[href], input, select, textarea, [tabindex="0"]') || [])];
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKey);
      previousFocus?.focus?.();
    };
  }, [selectedArtwork]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [finale, setFinale] = useState(null);

  useEffect(() => {
    if (user) {
      setGuestLoaded(true);
      return;
    }
    try {
      const saved = JSON.parse(localStorage.getItem(guestKey) || "null");
      if (saved && typeof saved === "object") {
        setGuestProgress({ ...emptyProgress, ...saved });
      }
    } catch {
      setGuestProgress(emptyProgress);
    }
    setGuestLoaded(true);
  }, [guestKey, user]);

  const contributorById = useMemo(
    () => Object.fromEntries(contributors.map((item) => [item.id, item])),
    [contributors]
  );
  const creditsByArtwork = useMemo(() => {
    const grouped = {};
    artworkCredits.forEach((credit) => {
      if (!grouped[credit.artwork_id]) grouped[credit.artwork_id] = [];
      grouped[credit.artwork_id].push(credit);
    });
    return grouped;
  }, [artworkCredits]);

  const currentProgress = user ? (progress || emptyProgress) : guestProgress;
  const discovered = useMemo(
    () => unique(currentProgress.discovered_artworks || []),
    [currentProgress.discovered_artworks]
  );
  const discoveredSet = useMemo(() => new Set(discovered), [discovered]);
  const favoriteId = currentProgress.favorite_artwork_id;
  const target = Number(walk.completion_target || 6);
  const discoveredCount = discovered.length;
  const ready = discoveredCount >= target && (!walk.require_favorite_for_completion || Boolean(favoriteId));
  const percent = Math.min(100, Math.round((discoveredCount / Math.max(target, 1)) * 100));

  const artistNames = useCallback((artwork) => {
    const creditNames = (creditsByArtwork[artwork.id] || [])
      .map((credit) => contributorById[credit.contributor_id]?.name);
    const primary = contributorById[artwork.contributor_id]?.name;
    return unique([...creditNames, primary]);
  }, [contributorById, creditsByArtwork]);

  const filteredArtworks = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("de");
    return artworks.filter((artwork) => {
      if (filter === "DISCOVERED" && !discoveredSet.has(artwork.id)) return false;
      if (filter === "FAVORITE" && favoriteId !== artwork.id) return false;
      if (!needle) return true;
      const names = artistNames(artwork).join(" ");
      return [artwork.inventory_number, artwork.title, artwork.year, names]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("de")
        .includes(needle);
    });
  }, [artworks, artistNames, discoveredSet, favoriteId, filter, query]);

  const saveGuest = (next) => {
    setGuestProgress(next);
    try {
      localStorage.setItem(guestKey, JSON.stringify(next));
    } catch {
      // Der Rundgang bleibt in dieser Sitzung nutzbar, auch wenn Speicher blockiert ist.
    }
  };

  const discoverArtwork = async (artwork) => {
    if (discoveredSet.has(artwork.id)) return;
    setBusy(`discover:${artwork.id}`);
    setError("");
    try {
      if (user) {
        const { data: saved } = await api.post(`/progress/${walk.id}/discover-artwork`, {
          artwork_id: artwork.id,
        });
        setProgress(saved);
      } else {
        saveGuest({
          ...currentProgress,
          discovered_artworks: unique([...discovered, artwork.id]),
        });
      }
    } catch (e) {
      setError(errMsg(e, "Das Kunstwerk konnte nicht gespeichert werden."));
    } finally {
      setBusy("");
    }
  };

  const favoriteArtwork = async (artwork) => {
    setBusy(`favorite:${artwork.id}`);
    setError("");
    try {
      if (user) {
        const { data: saved } = await api.post(`/progress/${walk.id}/favorite-artwork`, {
          artwork_id: artwork.id,
        });
        setProgress(saved);
      } else {
        saveGuest({
          ...currentProgress,
          discovered_artworks: unique([...discovered, artwork.id]),
          favorite_artwork_id: artwork.id,
        });
      }
    } catch (e) {
      setError(errMsg(e, "Das Lieblingswerk konnte nicht gespeichert werden."));
    } finally {
      setBusy("");
    }
  };

  const completeMuseum = async () => {
    if (!ready) return;
    setBusy("complete");
    setError("");
    try {
      const { data: result } = await api.post(`/progress/${walk.id}/complete`, {});
      setFinale(result);
      if (user) {
        setProgress((previous) => ({
          ...(previous || currentProgress),
          status: "COMPLETED",
        }));
      } else {
        saveGuest({ ...currentProgress, status: "COMPLETED" });
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(errMsg(e, "Der Museumsrundgang konnte nicht abgeschlossen werden."));
    } finally {
      setBusy("");
    }
  };

  if (finale) {
    const reward = finale.completion_reward || {};
    const xp = reward.xp_awarded || reward.xp_preview || walk.completion_xp || 100;
    return (
      <div data-testid="museum-finale">
        <TopBar back="/wege" title="Kunstpark entdeckt" />
        <div className="mx-auto max-w-2xl px-5 py-8">
          <div className="rounded-[2rem] bg-[var(--brand)] p-6 text-white">
            <p className="text-[10px] font-bold tracking-[0.2em] text-white/65">SR-002 · ABSCHLUSS</p>
            <h1 className="mt-2 font-serif text-4xl leading-tight">Dein Blick hat eine Spur hinterlassen.</h1>
            <p className="mt-3 text-sm leading-relaxed text-white/75">
              Du hast mindestens {target} Werke bewusst entdeckt und dein persönliches Lieblingswerk gewählt.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-bold">
              <Sparkles className="h-4 w-4" />
              {user ? (reward.xp_awarded ? `+${xp} SPUREN-XP` : `${reward.xp_balance || 0} SPUREN-XP gesammelt`) : `${xp} XP mit deinem Konto`}
            </div>
          </div>

          {reward.collectible ? (
            <section className="mt-7 rounded-3xl bg-[var(--brand-soft)] p-5">
              <p className="eyebrow">Deine digitale Sammelkarte · nur digital</p>
              <h2 className="mb-5 mt-1 text-2xl">
                {user ? "SR-002 wurde in deinem Profil gespeichert." : "So sieht deine SR-002 aus."}
              </h2>
              <CollectibleCard value={reward.collectible} testId="museum-collectible" />
            </section>
          ) : null}

          {!user ? (
            <div className="mt-5 rounded-2xl border bg-white p-5">
              <p className="eyebrow">Als Gast entdeckt</p>
              <p className="mt-2 text-sm leading-relaxed text-[var(--ink-soft)]">
                Deine Auswahl bleibt auf diesem Gerät. Melde dich an, damit Sammelkarte und XP dauerhaft in deinem Profil landen.
              </p>
              <Link to="/login" className="btn btn-primary mt-4 w-full">ANMELDEN & SAMMELN</Link>
            </div>
          ) : null}

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {user ? <Link to="/profil" className="btn btn-primary w-full">XP & KARTE IM PROFIL</Link> : null}
            <button className="btn btn-ghost w-full" onClick={() => setFinale(null)}>
              WEITERE WERKE ANSEHEN
            </button>
          </div>
        </div>
      </div>
    );
  }

  const favoriteArtworkItem = artworks.find((artwork) => artwork.id === favoriteId);
  const missing = Math.max(target - discoveredCount, 0);

  return (
    <div data-testid="museum-explorer">
      <TopBar back="/wege" title={walk.title} />

      <div className="relative h-[46vh] min-h-[330px] overflow-hidden bg-[var(--brand)] grain">
        {walk.hero_image ? (
          <img src={fileUrl(walk.hero_image)} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-2xl px-5 pb-7 text-white">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-[10px] font-bold tracking-[0.14em] backdrop-blur">
              <Images className="h-3.5 w-3.5" /> FREIES MUSEUM
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand-accent)] px-3 py-1.5 text-[10px] font-bold">
              <Sparkles className="h-3.5 w-3.5" /> +{walk.completion_xp ?? 100} XP
            </span>
          </div>
          <p className="mt-5 text-[10px] font-bold tracking-[0.2em] text-white/65">
            {walk.collectible_number || "SR-002"}
          </p>
          <h1 className="mt-1 font-serif text-4xl leading-none sm:text-5xl">{walk.title}</h1>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/80">
            Sieh hin. Finde deinen Blick. Sammle, was dich berührt.
          </p>
        </div>
      </div>

      <main className="mx-auto max-w-2xl px-5 pb-48 pt-7">
        <section className="rounded-[1.75rem] border bg-white p-5" data-testid="museum-progress">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="eyebrow">Dein Museumsrundgang</p>
              <h2 className="mt-1 text-2xl">Du bestimmst den Weg.</h2>
            </div>
            <span className="rounded-full bg-[var(--brand)] px-3 py-1.5 text-xs font-bold text-white">
              {discoveredCount >= target ? `${discoveredCount} entdeckt` : `${discoveredCount}/${target}`}
            </span>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-[var(--ink-soft)]">
            Öffne, was dich interessiert. Markiere mindestens {target} Werke als entdeckt und wähle anschließend dein Lieblingswerk.
          </p>
          <div className="mt-5 h-2 overflow-hidden rounded-full bg-[var(--brand-soft)]">
            <div className="h-full rounded-full bg-[var(--brand-accent)] transition-all" style={{ width: `${percent}%` }} />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div className={`rounded-2xl p-3 ${discoveredCount >= target ? "bg-[var(--brand-soft)] text-[var(--brand)]" : "bg-[#f5f2eb] text-[var(--ink-soft)]"}`}>
              <Check className="mb-2 h-4 w-4" />
              <strong className="block">{discoveredCount >= target ? "Auswahl geschafft" : `Noch ${missing} Werk${missing === 1 ? "" : "e"}`}</strong>
            </div>
            <div className={`rounded-2xl p-3 ${favoriteId ? "bg-[#f7e8e5] text-[#8c4038]" : "bg-[#f5f2eb] text-[var(--ink-soft)]"}`}>
              <Heart className={`mb-2 h-4 w-4 ${favoriteId ? "fill-current" : ""}`} />
              <strong className="block">{favoriteArtworkItem ? favoriteArtworkItem.title : "Liebling noch offen"}</strong>
            </div>
          </div>
          {ready ? (
            <div className="mt-5 rounded-2xl bg-[var(--brand)] p-5 text-white" data-testid="museum-ready">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-[10px] font-bold tracking-[0.15em] text-white/65">ZIEL ERREICHT</p>
                  <h3 className="mt-1 font-serif text-2xl">Du kannst jetzt abschließen.</h3>
                  <p className="mt-2 text-xs leading-relaxed text-white/75">
                    {user
                      ? `Du kannst weiter Werke entdecken oder dir jetzt ${walk.completion_xp ?? 100} XP und die digitale SR-002 sichern.`
                      : "Du kannst weiter Werke entdecken oder den Abschluss ansehen. Karte und XP werden nur mit einem Konto gespeichert."}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-xs font-bold tracking-wide text-[var(--brand)]"
                disabled={busy === "complete" || !guestLoaded}
                onClick={completeMuseum}
                data-testid="museum-complete-inline"
              >
                {busy === "complete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {user ? `JETZT ABSCHLIESSEN · +${walk.completion_xp ?? 100} XP` : "ABSCHLUSS ANSEHEN"}
              </button>
            </div>
          ) : null}
          {!user ? (
            <p className="mt-4 rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-xs leading-relaxed text-[var(--brand)]">
              Als Gast wird deine Auswahl auf diesem Gerät gespeichert. Für dauerhafte Karte und XP kannst du dich vor dem Abschluss anmelden.
            </p>
          ) : null}
        </section>

        <section className="mt-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="eyebrow">Die Sammlung</p>
              <h2 className="mt-1 text-3xl">Finde deinen Blick.</h2>
            </div>
            <span className="text-xs text-[var(--ink-soft)]">{filteredArtworks.length} Werke</span>
          </div>

          <label className="mt-4 flex items-center gap-3 rounded-2xl border bg-white px-4 py-3">
            <Search className="h-4 w-4 text-[var(--ink-soft)]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Werk oder Künstler:in suchen"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none"
              data-testid="museum-search"
            />
          </label>

          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {[
              ["ALL", "Alle"],
              ["DISCOVERED", `Entdeckt · ${discoveredCount}`],
              ["FAVORITE", "Mein Liebling"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setFilter(value)}
                className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold ${filter === value ? "bg-[var(--brand)] text-white" : "border bg-white text-[var(--ink-soft)]"}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {filteredArtworks.map((artwork) => {
              const isDiscovered = discoveredSet.has(artwork.id);
              const isFavorite = favoriteId === artwork.id;
              const names = artistNames(artwork);
              return (
                <button
                  key={artwork.id}
                  type="button"
                  onClick={() => setSelectedArtwork(artwork)}
                  className="card-lift group overflow-hidden rounded-2xl border bg-white text-left"
                  data-testid={`museum-artwork-${artwork.inventory_number}`}
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-[var(--brand-soft)]">
                    {artwork.image ? (
                      <img
                        src={fileUrl(artwork.image)}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
                      />
                    ) : null}
                    <div className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-1 text-[9px] font-bold tracking-wide text-[var(--brand)]">
                      {artwork.inventory_number}
                    </div>
                    {isDiscovered ? (
                      <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-[var(--brand)] px-2 py-1 text-[9px] font-bold text-white">
                        <Check className="h-3 w-3" /> ENTDECKT
                      </span>
                    ) : null}
                    {isFavorite ? (
                      <span className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#a54b43]">
                        <Heart className="h-4 w-4 fill-current" />
                      </span>
                    ) : null}
                  </div>
                  <div className="p-3">
                    <h3 className="line-clamp-2 font-serif text-lg leading-[1.05]">{artwork.title}</h3>
                    {names.length ? (
                      <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-[var(--ink-soft)]">{names.join(" · ")}</p>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>

          {!filteredArtworks.length ? (
            <p className="mt-5 rounded-2xl border bg-white p-5 text-sm text-[var(--ink-soft)]">
              Zu dieser Auswahl wurde kein Kunstwerk gefunden.
            </p>
          ) : null}
        </section>

        {discoveryItems.length ? (
          <section className="mt-10">
            <p className="eyebrow">Mehr im Kunstpark</p>
            <h2 className="mt-1 text-3xl">Nicht nur Kunstwerke.</h2>
            <div className="mt-4 space-y-3">
              {discoveryItems.map((item) => (
                <article key={item.id} className="flex gap-4 overflow-hidden rounded-2xl border bg-white p-3">
                  <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-[var(--brand-soft)]">
                    {item.image ? <img src={fileUrl(item.image)} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
                  </div>
                  <div className="min-w-0 py-1">
                    <p className="text-[9px] font-bold tracking-[0.15em] text-[var(--brand-accent)]">{item.inventory_number || item.content_type}</p>
                    <h3 className="mt-1 font-serif text-xl leading-none">{item.title}</h3>
                    {item.short_text ? <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-[var(--ink-soft)]">{item.short_text}</p> : null}
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {error ? <p className="mt-5 rounded-2xl bg-red-50 p-4 text-sm text-red-700">{error}</p> : null}
      </main>

      <div style={{ bottom: "calc(4.25rem + env(safe-area-inset-bottom, 0px))" }} className="fixed inset-x-0 z-40 border-t bg-[#f8f5ee]/95 px-4 py-3 shadow-[0_-8px_24px_rgba(18,63,53,0.08)] backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold tracking-[0.14em] text-[var(--ink-soft)]">DEIN ABSCHLUSS</p>
            <p className="truncate text-sm font-semibold">
              {ready ? (user ? `Ziel erreicht · +${walk.completion_xp ?? 100} XP & SR-002` : "Ziel erreicht · Abschluss ansehen") : missing ? `Noch ${missing} Werk${missing === 1 ? "" : "e"} entdecken` : "Wähle dein Lieblingswerk"}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary !px-5 !py-3 !text-xs"
            disabled={!ready || busy === "complete" || !guestLoaded}
            onClick={completeMuseum}
            data-testid="museum-complete"
          >
            {busy === "complete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            ABSCHLIESSEN
          </button>
        </div>
      </div>

      {selectedArtwork ? (
        <div ref={dialogRef} className="fixed inset-0 z-50 overflow-y-auto bg-[#f8f5ee]" role="dialog" aria-modal="true" aria-label={selectedArtwork.title} data-testid="museum-artwork-detail">
          <div className="sticky top-0 z-10 border-b bg-[#f8f5ee]/95 px-4 py-3 backdrop-blur">
            <div className="mx-auto flex max-w-2xl items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[9px] font-bold tracking-[0.15em] text-[var(--brand-accent)]">{selectedArtwork.inventory_number}</p>
                <p className="truncate font-serif text-lg">{selectedArtwork.title}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedArtwork(null)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-white"
                aria-label="Kunstwerk schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <article className="mx-auto max-w-2xl pb-10">
            <div className="flex snap-x snap-mandatory gap-2 overflow-x-auto bg-[#111] px-3 py-3">
              {unique([selectedArtwork.image, ...(selectedArtwork.gallery || [])]).map((image, index) => (
                <img
                  key={image}
                  src={fileUrl(image)}
                  alt=""
                  className="h-[58vh] min-h-[380px] w-[88%] shrink-0 snap-center rounded-xl object-contain sm:w-[72%]"
                  loading={index ? "lazy" : "eager"}
                />
              ))}
            </div>

            <div className="px-5 py-7">
              <p className="eyebrow">{selectedArtwork.inventory_number}</p>
              <h2 className="mt-1 font-serif text-4xl leading-none">{selectedArtwork.title}</h2>
              {artistNames(selectedArtwork).length ? (
                <p className="mt-3 text-sm font-semibold text-[var(--brand)]">
                  {artistNames(selectedArtwork).join(" · ")}
                </p>
              ) : null}

              <div className="mt-5 flex flex-wrap gap-2 text-xs text-[var(--ink-soft)]">
                {selectedArtwork.year ? <span className="rounded-full bg-white px-3 py-1.5">{selectedArtwork.year}</span> : null}
                {selectedArtwork.material ? <span className="rounded-full bg-white px-3 py-1.5">{selectedArtwork.material}</span> : null}
                {selectedArtwork.dimensions ? <span className="rounded-full bg-white px-3 py-1.5">{selectedArtwork.dimensions}</span> : null}
              </div>

              {selectedArtwork.short_description ? (
                <p className="mt-6 text-base leading-relaxed">{selectedArtwork.short_description}</p>
              ) : null}
              {selectedArtwork.description ? (
                <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-[var(--ink-soft)]">{selectedArtwork.description}</p>
              ) : null}
              {selectedArtwork.artist_statement ? (
                <blockquote className="mt-6 border-l-2 border-[var(--brand-accent)] pl-4 font-serif text-xl leading-snug">
                  {selectedArtwork.artist_statement}
                </blockquote>
              ) : null}
              {selectedArtwork.interpretation_prompt ? (
                <div className="mt-7 rounded-2xl bg-[var(--brand)] p-5 text-white">
                  <p className="text-[9px] font-bold tracking-[0.16em] text-white/60">DEIN BLICK</p>
                  <p className="mt-2 font-serif text-2xl leading-snug">{selectedArtwork.interpretation_prompt}</p>
                </div>
              ) : null}

              {(creditsByArtwork[selectedArtwork.id] || []).length ? (
                <div className="mt-7 border-t pt-5">
                  <p className="eyebrow">Mitwirkende</p>
                  {(creditsByArtwork[selectedArtwork.id] || []).map((credit) => (
                    <p key={credit.id} className="mt-2 text-sm">{credit.credit_label}</p>
                  ))}
                </div>
              ) : null}

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  className={`btn w-full ${discoveredSet.has(selectedArtwork.id) ? "btn-ghost" : "btn-primary"}`}
                  disabled={discoveredSet.has(selectedArtwork.id) || busy === `discover:${selectedArtwork.id}`}
                  onClick={() => discoverArtwork(selectedArtwork)}
                  data-testid="museum-mark-discovered"
                >
                  {busy === `discover:${selectedArtwork.id}` ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  {discoveredSet.has(selectedArtwork.id) ? "ENTDECKT" : "ALS ENTDECKT MARKIEREN"}
                </button>
                <button
                  type="button"
                  className={`btn w-full ${favoriteId === selectedArtwork.id ? "bg-[#a54b43] text-white" : "btn-ghost"}`}
                  disabled={busy === `favorite:${selectedArtwork.id}`}
                  onClick={() => favoriteArtwork(selectedArtwork)}
                  data-testid="museum-mark-favorite"
                >
                  {busy === `favorite:${selectedArtwork.id}` ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Heart className={`h-4 w-4 ${favoriteId === selectedArtwork.id ? "fill-current" : ""}`} />
                  )}
                  {favoriteId === selectedArtwork.id ? "MEIN LIEBLINGSWERK" : "ZUM LIEBLINGSWERK"}
                </button>
              </div>
              {ready ? (
                <div className="mt-4 rounded-2xl bg-[var(--brand)] p-4 text-white" data-testid="museum-ready-detail">
                  <p className="text-xs leading-relaxed text-white/75">
                    {user
                      ? `Ziel erreicht. Du kannst weiterentdecken oder jetzt deine digitale SR-002 und ${walk.completion_xp ?? 100} XP sichern.`
                      : "Ziel erreicht. Du kannst weiterentdecken oder den Abschluss ansehen. Karte und XP werden nur mit einem Konto gespeichert."}
                  </p>
                  <button
                    type="button"
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-xs font-bold text-[var(--brand)]"
                    disabled={busy === "complete" || !guestLoaded}
                    onClick={completeMuseum}
                    data-testid="museum-complete-detail"
                  >
                    {busy === "complete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {user ? `RUNDE ABSCHLIESSEN · +${walk.completion_xp ?? 100} XP` : "ABSCHLUSS ANSEHEN"}
                  </button>
                </div>
              ) : null}
              {error ? <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
            </div>
          </article>
        </div>
      ) : null}
    </div>
  );
}
